import cn from 'classnames'
import {CSSProperties, FC, ReactNode} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Link} from '@/i18n'
import {HEADER_HEIGHT_DESKTOP, HERO_PADDING_DESKTOP, HERO_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {QueryLink} from '@/utils/constants'
import {MOBILE_SCREENSIZE, TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {Section} from '../content/Section'
import {Text, TextFontWeight, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'

export type HeroProps = {
  title: ReactNode
  subtitle?: string
  subtitleColor?: string
  primaryButtonLink?: string | QueryLink
  primaryButtonText?: string
  primaryButtonTrackedEvent?: string
  isCentered?: boolean
  secondaryButtonLink?: string | QueryLink
  secondaryButtonText?: string
  secondaryButtonTrackedEvent?: string
  secondaryButtonVariant?: ButtonVariant
  backgroundColor?: string
  handleClickOnPrimaryButton?: () => void
  handleClickOnSecondaryButton?: () => void
  note?: string
  isFullWidth?: boolean
  benefits?: ReactNode
  className?: string
  maxWidth?: string
  alignItems?: CSSProperties['alignItems']
  justifyContent?: CSSProperties['justifyContent']
  breadcrumbs?: ReactNode
  metaInfo?: ReactNode
  isVerticallyCentered?: boolean
}

export const Hero: FC<React.PropsWithChildren<HeroProps>> = ({
  title,
  subtitle,
  subtitleColor = theme.palette.text.light,
  primaryButtonLink,
  primaryButtonText,
  primaryButtonTrackedEvent,
  isCentered = false,
  children,
  secondaryButtonLink,
  secondaryButtonText,
  secondaryButtonTrackedEvent,
  secondaryButtonVariant = ButtonVariant.secondary,
  backgroundColor = theme.palette.background.butter,
  handleClickOnPrimaryButton,
  handleClickOnSecondaryButton,
  note,
  isFullWidth = false,
  benefits,
  className,
  maxWidth = '765px',
  alignItems = 'flex-end',
  justifyContent = 'space-between',
  breadcrumbs,
  metaInfo,
  isVerticallyCentered = true,
}) => {
  const trackClick = (event: string) => {
    analytics.track(event)
  }

  return (
    <>
      <Section backgroundColor={backgroundColor} className={className}>
        <div className={cn('wrapper', isCentered && 'centered', isFullWidth && 'full-width')}>
          <div className="content">
            {breadcrumbs && <div className="breadcrumbs">{breadcrumbs}</div>}
            <Text variant={TextVariant.h1}>{title}</Text>
            {subtitle && (
              <div className="subtitle">
                <Text
                  variant={TextVariant.text24}
                  fontWeight={TextFontWeight.light}
                  color={subtitleColor}
                >
                  {subtitle}
                </Text>
              </div>
            )}
            {benefits}
            <div className="buttons">
              {primaryButtonText && (
                <div className="button-wrapper">
                  {primaryButtonLink ? (
                    <Link href={primaryButtonLink} legacyBehavior>
                      <a
                        data-test="GetStartedHero"
                        // TODO improve names for tracked events on other pages as well
                        onClick={() => trackClick(primaryButtonTrackedEvent || 'HeroCTAClicked')}
                        role="button"
                        tabIndex={0}
                      >
                        <Button
                          variant={ButtonVariant.primary}
                          type="button"
                          fullWidth
                          icon={<ArrowIcon color={theme.palette.text.white} />}
                          isIconAnimated
                        >
                          {primaryButtonText}
                        </Button>
                      </a>
                    </Link>
                  ) : handleClickOnPrimaryButton ? (
                    <Button
                      variant={ButtonVariant.primary}
                      type="button"
                      fullWidth
                      onClick={handleClickOnPrimaryButton}
                      icon={<ArrowIcon color={theme.palette.text.white} />}
                      isIconAnimated
                    >
                      {primaryButtonText}
                    </Button>
                  ) : null}
                </div>
              )}
              {secondaryButtonText && (
                <div className="button-wrapper">
                  {secondaryButtonLink ? (
                    <Link href={secondaryButtonLink} legacyBehavior>
                      <a
                        data-test="GetStartedHero-secondary"
                        onClick={() =>
                          secondaryButtonTrackedEvent
                            ? trackClick(secondaryButtonTrackedEvent)
                            : undefined
                        }
                        role="button"
                        tabIndex={0}
                      >
                        <Button variant={secondaryButtonVariant} type="button" fullWidth>
                          {secondaryButtonText}
                        </Button>
                      </a>
                    </Link>
                  ) : handleClickOnSecondaryButton ? (
                    <Button
                      variant={secondaryButtonVariant}
                      type="button"
                      fullWidth
                      onClick={handleClickOnSecondaryButton}
                    >
                      {secondaryButtonText}
                    </Button>
                  ) : null}
                </div>
              )}
            </div>
            {note && (
              <div className="note">
                <Text variant={TextVariant.caption} element="p" color={theme.palette.text.light}>
                  {note}
                </Text>
              </div>
            )}
            {metaInfo}
          </div>
          {children}
        </div>
      </Section>

      <style jsx>{`
        .breadcrumbs {
          margin-bottom: ${theme.spacing[5]};
        }
        .wrapper {
          display: flex;
          justify-content: ${justifyContent};
          align-items: ${alignItems};
          position: relative;
          padding: calc(${HEADER_HEIGHT_DESKTOP} + ${HERO_PADDING_DESKTOP}) 0
            ${HERO_PADDING_DESKTOP};
          margin-top: -${HEADER_HEIGHT_DESKTOP};
        }
        .content {
          flex: 1;
          margin-top: ${isVerticallyCentered ? 'auto' : 0};
          margin-bottom: ${isVerticallyCentered ? 'auto' : 0};
        }
        .content:not(.full-width) {
          max-width: ${maxWidth};
        }
        .subtitle {
          margin-top: ${theme.spacing[4]};
        }
        .buttons {
          display: flex;
          align-items: flex-start;
        }
        .button-wrapper {
          margin-top: ${theme.spacing[5]};
        }
        .button-wrapper + .button-wrapper {
          margin-left: ${theme.spacing[2]};
        }
        .centered {
          display: block;
          margin-left: auto;
          margin-right: auto;
          text-align: center;
        }
        .centered:not(.full-width) {
          max-width: 750px;
        }
        .centered .content {
          margin: 0 auto;
        }
        .note {
          max-width: 380px;
          margin-top: ${theme.spacing[2]};
        }
        @media ${TABLET_SCREENSIZE} {
          .wrapper {
            flex-direction: column;
            justify-content: ${breadcrumbs ? 'flex-start' : 'center'};
            align-items: center;
            padding: ${HERO_PADDING_DESKTOP} 0;
            margin-top: 0;
          }
          .content {
            max-width: 700px;
            text-align: ${breadcrumbs ? 'left' : 'center'};
            ${breadcrumbs ? `width: 100%` : ''};
          }
          .buttons {
            justify-content: center;
          }
          .note {
            margin-left: auto;
            margin-right: auto;
          }
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            padding: ${HERO_PADDING_MOBILE} 0;
          }
          .subtitle {
            margin-top: ${theme.spacing[3]};
          }
          .buttons {
            display: block;
          }
          .button-wrapper {
            margin-top: ${theme.spacing[4]};
          }
          .button-wrapper + .button-wrapper {
            margin-left: 0;
            margin-top: ${theme.spacing[3]};
          }
        }
      `}</style>
    </>
  )
}
